import * as React from 'react';
import { Checkbox as BaseCheckbox } from '@base-ui-components/react/checkbox';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import styles from './Checkbox.module.css';

/**
 * Чекбокс Luma поверх Base UI.
 *
 * Заведён потому, что до сих пор по админке рассыпаны сырые
 * `<input type="checkbox">`: они не берут токены темы и выглядят системно
 * (разный вид в macOS/Windows), а нативный чекбокс не даёт indeterminate без
 * ref-возни.
 *
 * Два уровня API:
 *   <Checkbox checked={v} onCheckedChange={setV} />            — голый бокс;
 *   <Checkbox checked={v} onCheckedChange={setV} label="…" />  — с подписью,
 *   клик по тексту переключает (обёрнуто в <label>).
 *
 * @use флажок в форме или списке — вместо сырого `<input type="checkbox">`, который не берёт токены темы и не умеет indeterminate.
 */

export interface CheckboxProps
  extends Omit<React.ComponentProps<typeof BaseCheckbox.Root>, 'render'> {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  className?: string;
}

export const Checkbox = React.forwardRef<HTMLElement, CheckboxProps>(function Checkbox(
  { label, hint, className, disabled, ...rest },
  ref,
) {
  const box = (
    <BaseCheckbox.Root
      ref={ref}
      disabled={disabled}
      className={cn(styles.root, !label && className)}
      {...rest}
    >
      <BaseCheckbox.Indicator className={styles.indicator}>
        {rest.indeterminate ? <Minus size={13} strokeWidth={3} /> : <Check size={13} strokeWidth={3} />}
      </BaseCheckbox.Indicator>
    </BaseCheckbox.Root>
  );

  if (!label) return box;

  return (
    <label className={cn(styles.label, className)} data-disabled={disabled || undefined}>
      {box}
      <span className={styles.labelText}>
        {label}
        {hint && <span className={styles.hint}>{hint}</span>}
      </span>
    </label>
  );
});
